import { useState } from 'react';
import { MOBILE_REDIRECT } from '@kaba/shared';
import { api } from '../lib/api';
import { signOut, useMe, usePublicConfig } from '../lib/auth';
import { Button } from './ui/ui';
import { Shell } from './LoginPage';

/** Gültige Anfrage der App: /app-verbinden?challenge=…&state=… */
export function appLinkParams(search = location.search): { challenge: string; state: string } | null {
  const p = new URLSearchParams(search);
  const challenge = p.get('challenge') ?? '';
  const state = p.get('state') ?? '';
  if (!/^[A-Za-z0-9_-]{43}$/.test(challenge) || !/^[A-Za-z0-9_-]{16,128}$/.test(state)) return null;
  return { challenge, state };
}

const back = (q: Record<string, string>) => `${MOBILE_REDIRECT}?${new URLSearchParams(q).toString()}`;

/**
 * Browser-Anmeldung für die KaBa-App: Nach der normalen Anmeldung (Passwort, SSO oder Passkey)
 * bestätigt die Person ausdrücklich, dass die App dieses Konto auf dieser Instanz verwenden darf.
 * In die App zurück geht nur ein kurzlebiger Einmalcode – nie die Sitzung.
 */
export function AppLinkPage() {
  const { data: me } = useMe();
  const { data: cfg } = usePublicConfig();
  const params = appLinkParams();
  const [state, setState] = useState<'ask' | 'busy' | 'done' | 'error'>('ask');
  const [error, setError] = useState<string | null>(null);

  if (!params)
    return (
      <Shell title="KaBa-App verbinden">
        <p className="text-sm">Dieser Link ist unvollständig. Bitte starte die Anmeldung erneut in der KaBa-App.</p>
      </Shell>
    );
  if (!me) return null;
  if (me.isGuest)
    return (
      <Shell title="KaBa-App verbinden">
        <p className="text-sm">Mit einem Gastzugang kann die App nicht verbunden werden. Bitte melde dich mit einem eigenen Konto an.</p>
        <Button onClick={() => void signOut(location.pathname + location.search)}>Abmelden</Button>
      </Shell>
    );

  const connect = async () => {
    setState('busy');
    setError(null);
    try {
      const { code } = await api.post<{ code: string }>('/api/mobile/handoff', { challenge: params.challenge, redirect: MOBILE_REDIRECT });
      // Seite verlassen, damit die Adresse mit der Anfrage nicht im Verlauf erneut ausgelöst wird
      history.replaceState(null, '', '/app-verbinden');
      location.href = back({ code, state: params.state });
      setState('done');
    } catch (err) {
      setError((err as Error).message);
      setState('error');
    }
  };

  const host = location.host;
  if (state === 'done')
    return (
      <Shell title="KaBa-App verbinden">
        <p className="text-sm">Die App wurde verbunden. Du kannst dieses Fenster schließen und zur KaBa-App zurückkehren.</p>
      </Shell>
    );

  return (
    <Shell title="KaBa-App verbinden">
      <p className="text-sm">
        Die KaBa-App auf deinem Gerät möchte dein Konto <strong>{me.name}</strong> ({me.email}) auf <strong>{cfg?.instanceName ?? 'KaBa'}</strong> ({host}) verwenden.
      </p>
      <p className="text-sm text-[var(--ink-soft)]">
        Verbinde nur, wenn du die Anmeldung gerade selbst in der App gestartet hast. Du kannst die App jederzeit im Profil unter „Angemeldete Apps“ wieder abmelden.
      </p>
      {error && (
        <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </p>
      )}
      <Button variant="primary" className="min-h-12" disabled={state === 'busy'} onClick={() => void connect()}>
        App verbinden
      </Button>
      <Button className="min-h-11" disabled={state === 'busy'} onClick={() => void signOut(location.pathname + location.search)}>
        Anderes Konto verwenden
      </Button>
      <Button variant="ghost" className="min-h-11" onClick={() => (location.href = back({ error: 'cancelled', state: params.state }))}>
        Abbrechen
      </Button>
    </Shell>
  );
}
